/** The catalog of the alert sounds: 37 named sounds in five groups (Classic, Soft, Nature, Voices, Funny), the way
 * TradingView's alert window lists them (operator, 2026-11-04). Each is data, played by one small
 * engine, so a sound can be added by adding a line:
 *
 *   - `tones`  notes made in the browser (frequency, start, length, wave, loudness, end frequency),
 *   - `noise`  filtered noise (thunder, glass, applause),
 *   - `speech` words said by the browser's voice.
 *
 * Real recordings are not in the code: they are files in the server's sounds folder that the operator
 * has approved (docs/alert_sounds.md). Their ids start with `file:` and play from the server.
 *
 * Browsers only allow sound after the person has done something on the page; every function here
 * stays quiet when audio is not allowed or not there.
 */

export const SOUND_GROUPS = ["Classic", "Soft", "Nature", "Voices", "Funny"] as const;
export type SoundGroup = (typeof SOUND_GROUPS)[number];

export type Wave = OscillatorType;
/** [frequency Hz, start s, length s, wave, loudness, frequency Hz it glides to] */
export type Tone = readonly [number, number, number, Wave?, number?, number?];
/** [start s, length s, filter Hz, loudness, filter kind] */
export type Noise = readonly [number, number, number, number, BiquadFilterType];

export interface Recipe {
  tones?: readonly Tone[];
  noise?: readonly Noise[];
  speech?: string;
}

export interface SoundDef extends Recipe {
  id: string;
  label: string;
  group: SoundGroup;
}

/** Notes of a rising or falling run: each `step` Hz from `from`, one every `gap` seconds. */
function run(from: number, step: number, count: number, gap: number, length: number): Tone[] {
  return Array.from({ length: count }, (_, i): Tone => [
    from + step * i,
    gap * i,
    length,
    "triangle",
  ]);
}

/** A crowd: many short bursts of noise, loudest in the middle. */
function crowd(): Noise[] {
  return Array.from({ length: 48 }, (_, i): Noise => {
    const at = (i * 1.7) / 48 + ((i * 37) % 11) / 400;
    return [at, 0.04, 2400 + ((i * 53) % 7) * 250, 0.35 - Math.abs(i - 24) * 0.006, "bandpass"];
  });
}

const CLASSIC: SoundDef[] = [
  {
    id: "thin",
    label: "Thin",
    group: "Classic",
    tones: [
      [1500, 0, 0.1],
      [1500, 0.14, 0.1],
    ],
  },
  {
    id: "three-notes-reverb",
    label: "3 Notes Reverb",
    group: "Classic",
    tones: [
      [523, 0, 0.5],
      [659, 0.2, 0.5],
      [784, 0.4, 0.9],
    ],
  },
  {
    id: "alarm-clock",
    label: "Alarm Clock",
    group: "Classic",
    tones: Array.from({ length: 8 }, (_, i): Tone => [1000, i * 0.16, 0.08, "square", 0.1]),
  },
  {
    id: "beep-beep",
    label: "Beep-beep",
    group: "Classic",
    tones: [
      [880, 0, 0.12, "square", 0.12],
      [880, 0.2, 0.12, "square", 0.12],
    ],
  },
  {
    id: "calling",
    label: "Calling",
    group: "Classic",
    tones: [
      [440, 0, 0.45],
      [480, 0, 0.45],
      [440, 0.65, 0.45],
      [480, 0.65, 0.45],
    ],
  },
  {
    id: "chirpy",
    label: "Chirpy",
    group: "Classic",
    tones: [
      [1800, 0, 0.07, "sine", 0.2, 2600],
      [2400, 0.1, 0.07, "sine", 0.2, 3000],
      [2000, 0.2, 0.09],
    ],
  },
  {
    id: "fault",
    label: "Fault",
    group: "Classic",
    tones: [
      [150, 0, 0.18, "sawtooth", 0.15],
      [110, 0.2, 0.35, "sawtooth", 0.15],
    ],
  },
  {
    id: "hand-bell",
    label: "Hand Bell",
    group: "Classic",
    tones: [
      [1319, 0, 0.9],
      [2637, 0, 0.5, "sine", 0.08],
    ],
  },
];

const SOFT: SoundDef[] = [
  {
    id: "banjo",
    label: "Banjo",
    group: "Soft",
    tones: [
      [392, 0, 0.3, "triangle"],
      [494, 0.12, 0.3, "triangle"],
      [587, 0.24, 0.4, "triangle"],
    ],
  },
  {
    id: "droplet",
    label: "Droplet",
    group: "Soft",
    tones: [[1200, 0, 0.1, "sine", 0.25, 500]],
  },
  {
    id: "flickering",
    label: "Flickering",
    group: "Soft",
    tones: [
      [1200, 0, 0.05],
      [900, 0.07, 0.05],
      [1500, 0.14, 0.05],
      [1000, 0.21, 0.05],
      [1300, 0.28, 0.07],
    ],
  },
  {
    id: "hoarse",
    label: "Hoarse",
    group: "Soft",
    tones: [[190, 0, 0.35, "sawtooth", 0.08, 170]],
    noise: [[0, 0.35, 1800, 0.12, "bandpass"]],
  },
  {
    id: "knock-knock",
    label: "Knock-knock",
    group: "Soft",
    tones: [
      [130, 0, 0.08, "sine", 0.5, 90],
      [120, 0.2, 0.08, "sine", 0.5, 80],
    ],
    noise: [
      [0, 0.03, 600, 0.3, "lowpass"],
      [0.2, 0.03, 600, 0.3, "lowpass"],
    ],
  },
  {
    id: "promise",
    label: "Promise",
    group: "Soft",
    tones: [
      [587, 0, 0.2],
      [784, 0.2, 0.5],
    ],
  },
  {
    id: "trumpets",
    label: "Trumpets",
    group: "Soft",
    tones: [
      [392, 0, 0.35, "sawtooth", 0.06],
      [494, 0, 0.35, "sawtooth", 0.06],
      [523, 0.4, 0.6, "sawtooth", 0.06],
      [659, 0.4, 0.6, "sawtooth", 0.06],
    ],
  },
  {
    id: "you-win",
    label: "You win",
    group: "Soft",
    tones: [
      ...run(523, 0, 1, 0, 0.12),
      [659, 0.12, 0.12, "triangle"],
      [784, 0.24, 0.12, "triangle"],
      [1047, 0.36, 0.5, "triangle"],
    ],
  },
];

const NATURE: SoundDef[] = [
  {
    id: "bullfrog",
    label: "Bullfrog",
    group: "Nature",
    tones: [
      [110, 0, 0.14, "square", 0.12, 90],
      [95, 0.17, 0.18, "square", 0.12, 75],
    ],
    noise: [[0, 0.3, 400, 0.08, "lowpass"]],
  },
  {
    id: "cat",
    label: "Cat",
    group: "Nature",
    tones: [
      [500, 0, 0.25, "sawtooth", 0.1, 900],
      [900, 0.25, 0.35, "sawtooth", 0.1, 450],
    ],
  },
  {
    id: "dog",
    label: "Dog",
    group: "Nature",
    tones: [
      [420, 0, 0.12, "sawtooth", 0.18, 260],
      [420, 0.22, 0.12, "sawtooth", 0.18, 260],
    ],
    noise: [
      [0, 0.1, 900, 0.2, "bandpass"],
      [0.22, 0.1, 900, 0.2, "bandpass"],
    ],
  },
  {
    id: "raven",
    label: "Raven",
    group: "Nature",
    tones: [
      [520, 0, 0.28, "sawtooth", 0.14, 300],
      [500, 0.4, 0.28, "sawtooth", 0.14, 290],
    ],
    noise: [
      [0, 0.28, 1400, 0.1, "bandpass"],
      [0.4, 0.28, 1400, 0.1, "bandpass"],
    ],
  },
  {
    id: "rooster",
    label: "Rooster",
    group: "Nature",
    tones: [
      [600, 0, 0.16, "sawtooth", 0.1, 850],
      [800, 0.2, 0.16, "sawtooth", 0.1, 520],
      [700, 0.42, 0.2, "sawtooth", 0.1, 950],
      [950, 0.7, 0.7, "sawtooth", 0.1, 480],
    ],
  },
  {
    id: "thunder",
    label: "Thunder",
    group: "Nature",
    noise: [
      [0, 2.2, 220, 0.9, "lowpass"],
      [0.5, 1.5, 120, 0.7, "lowpass"],
    ],
  },
  {
    id: "water-drop",
    label: "Water drop",
    group: "Nature",
    tones: [
      [900, 0, 0.1, "sine", 0.3, 300],
      [1100, 0.28, 0.1, "sine", 0.18, 400],
    ],
  },
  {
    id: "whale",
    label: "Whale",
    group: "Nature",
    tones: [
      [200, 0, 1.4, "sine", 0.2, 420],
      [420, 1.1, 1.4, "sine", 0.2, 150],
    ],
  },
];

const VOICES: SoundDef[] = [
  { id: "attention", label: "Attention", group: "Voices", speech: "Attention" },
  { id: "guess-what", label: "Guess what", group: "Voices", speech: "Guess what?" },
  { id: "hey-pssst", label: "Hey, pssst", group: "Voices", speech: "Hey, pssst" },
  { id: "hey-take-a-look", label: "Hey, take a look", group: "Voices", speech: "Hey, take a look" },
  { id: "hey", label: "Hey!", group: "Voices", speech: "Hey!" },
  { id: "look-at-me", label: "Look at me", group: "Voices", speech: "Look at me" },
  { id: "sexy-mmm", label: "Sexy mmm", group: "Voices", speech: "Mmmm, hello" },
];

const FUNNY: SoundDef[] = [
  { id: "applause", label: "Applause", group: "Funny", noise: crowd() },
  {
    id: "breaking-glass",
    label: "Breaking glass",
    group: "Funny",
    noise: [
      [0, 0.08, 6500, 0.6, "highpass"],
      [0.1, 0.05, 7500, 0.3, "highpass"],
      [0.2, 0.05, 7000, 0.25, "highpass"],
    ],
    tones: [
      [3200, 0.05, 0.12, "sine", 0.1],
      [4100, 0.16, 0.1, "sine", 0.08],
      [3700, 0.26, 0.1, "sine", 0.06],
    ],
  },
  {
    id: "cash-register",
    label: "Cash register",
    group: "Funny",
    tones: [
      [1200, 0, 0.06, "square", 0.1],
      [2400, 0.1, 0.9, "sine", 0.22],
      [3200, 0.1, 0.5, "sine", 0.08],
    ],
    noise: [[0, 0.04, 3000, 0.25, "highpass"]],
  },
  {
    id: "chimes-power-down",
    label: "Chimes power down",
    group: "Funny",
    tones: [
      [1047, 0, 0.3],
      [784, 0.25, 0.3],
      [523, 0.5, 0.3],
      [330, 0.75, 0.7],
    ],
  },
  {
    id: "dramatic-one-note",
    label: "Dramatic one note",
    group: "Funny",
    tones: [
      [110, 0, 1.8, "sawtooth", 0.2],
      [165, 0, 1.8, "sawtooth", 0.1],
      [220, 0, 1.8, "sawtooth", 0.06],
    ],
  },
  { id: "jackpot", label: "Jackpot", group: "Funny", tones: run(523, 60, 14, 0.07, 0.12) },
];

/** The two-note chimes the dashboard had before this list: kept so a saved choice still plays. */
const LEGACY: SoundDef[] = [
  {
    id: "bright",
    label: "Bright",
    group: "Classic",
    tones: [
      [660, 0, 0.14],
      [880, 0.13, 0.18],
    ],
  },
  {
    id: "deep",
    label: "Deep",
    group: "Classic",
    tones: [
      [330, 0, 0.14],
      [440, 0.13, 0.18],
    ],
  },
  {
    id: "triad",
    label: "Triad",
    group: "Classic",
    tones: [
      [523, 0, 0.14],
      [784, 0.13, 0.18],
    ],
  },
  {
    id: "ping",
    label: "Ping",
    group: "Classic",
    tones: [
      [988, 0, 0.14],
      [1319, 0.13, 0.18],
    ],
  },
];

export const SOUNDS: readonly SoundDef[] = [
  ...CLASSIC,
  ...LEGACY,
  ...SOFT,
  ...NATURE,
  ...VOICES,
  ...FUNNY,
];

/** Sentinel id meaning "play nothing". */
export const MUTE_SOUND = "mute";

export function soundsOf(group: SoundGroup): SoundDef[] {
  return SOUNDS.filter((s) => s.group === group);
}

export function findSound(id: string): SoundDef | undefined {
  return SOUNDS.find((s) => s.id === id);
}
